import { deepClean } from "@contentful-api/helpers/deepClean";
import type {
    IPageCampaigns,
    IPageCheckout,
    IPageExplore,
    IPageGeneric,
    IPageHomepage,
    IPagePathFields,
    IPageProductCategoryPage,
    IPageProductListingPage,
    IPageStories,
    IPageTwoColumns,
} from "@contentful-api/types/contentful";
import { getPagePathByLocale, type PagePath } from "@lib/constants/pagePaths";
import type { PageTypes } from "@lib/enums/PageTypes";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { getCompleteSlug } from "@lib/utils/getCompleteSlug";
import { CMSWrapper } from "@lib/wrappers/cmsWrapper";
import type { Entry } from "contentful";

type Type =
    | IPageCampaigns
    | IPageCheckout
    | IPageExplore
    | IPageGeneric
    | IPageHomepage
    | IPageProductCategoryPage
    | IPageProductListingPage
    | IPageStories
    | IPageTwoColumns
    | undefined;

interface PagePathContentProps {
    locale: string;
    localeTagId: string;
    pageType: PageTypes;
    basePath: PagePath;
    slug?: string | string[];
}

const fetchPagePath = async (
    locale: string,
    localeTagId: string,
    pathName: string,
    level: number
): Promise<Entry<IPagePathFields> | null> => {
    const { items } = await CMSWrapper.getInstance().getEntries<IPagePathFields>({
        content_type: "pagePath",
        "metadata.tags.sys.id[in]": localeTagId,
        "fields.completeSlug": pathName,
        include: level,
        limit: 1,
        locale,
    });

    return items[0] ?? null;
};

export const getPageContentBySlug = async ({
    locale,
    localeTagId,
    pageType,
    basePath,
    slug,
}: PagePathContentProps): Promise<Entry<IPagePathFields> | null> => {
    if (!basePath) return null;

    const startTime = performance.now();

    // Try to fetch for the market-specific locale
    let pagePath = await fetchPagePath(
        locale,
        localeTagId,
        getCompleteSlug(slug, getPagePathByLocale(basePath, locale)),
        10
    );

    // If not found, try to fetch for the fallback locale
    if (!pagePath) {
        pagePath = await fetchPagePath(
            "en",
            localeTagId,
            getCompleteSlug(slug, getPagePathByLocale(basePath)),
            1
        );

        if (!pagePath?.fields?.page) {
            return null;
        }

        // If PagePath exists, try to switch locale and fetch the translated page
        const translatedPageData = await CMSWrapper.getInstance().getEntry<Type>(
            pagePath.fields.page.sys.id,
            {
                locale,
                include: 10, // Now resolve the linked entries
            }
        );

        // If the translation matches the requested locale, we update the pagePath
        if (translatedPageData?.sys?.locale === locale) {
            pagePath.fields.page = translatedPageData as unknown as Type;
        }
    }
    Logger.info(ServiceType.CONTENTFUL, "PagePath Contentful Query", {
        tag: LogTag.PERFORMANCE,
        duration: performance.now() - startTime,
        pageType,
    });

    return deepClean(pagePath) as Entry<IPagePathFields>;
};

interface IGetPagePathCompleteSlugs {
    locale: string;
    localeTagId: string;
}

export const getAllPagePaths = async ({
    locale,
    localeTagId,
}: IGetPagePathCompleteSlugs): Promise<Entry<IPagePathFields>[]> => {
    const allPagePathsByCurrentLocaleData =
        await CMSWrapper.getInstance().getEntries<IPagePathFields>({
            content_type: "pagePath",
            "metadata.tags.sys.id[in]": localeTagId,
            include: 5,
            limit: 1000,
            locale,
        });

    return allPagePathsByCurrentLocaleData?.items || [];
};
